// @flow
import { takeEvery, call, put, select, race, take, all } from 'redux-saga/effects';
import { CommonActions } from '@react-navigation/native';
import { setLoading } from '../LoadingStatus/actions';
import { tripica } from '../../api';
import { initNewOfferWithSelectedSubscription } from '../NewOffer/sagas';
import { fetchCustomerFailure, setCustomer, setCustomerOptionCommercial } from './actions';
import { customerSelector } from './selectors';
import HandleErrorService from '../../lib/handleError';
import { selectSubscriptionForActivation } from '../SignUp/sagas';
import { fetchAll as fetchAllSubscriptions } from '../Subscriptions';
import Navigation from '../../lib/navigation';

import type { CustomerActionType } from './actions';
import WizzeeLog from '../../lib/wizzeeLog';

export function* getCustomerOffer(): Generator<*, *, *> {
  yield put(fetchAllSubscriptions());
  yield race({
    success: take('SUBSCRIPTION_FETCH_ALL_SUCCESS'),
    error: take('SUBSCRIPTION_FETCH_ALL_FAILURE'),
  });

  yield call(selectSubscriptionForActivation);
  yield call(initNewOfferWithSelectedSubscription);
  yield call(fetchCustomerSaga);
}

export function* fetchCustomerSaga(): Generator<*, *, *> {
  yield put(setLoading('fetchCustomer', true));

  try {
    const customer = yield call([tripica, 'getCustomer']);
    yield put(setCustomer(customer));
  } catch (error) {
    WizzeeLog.warn('[saga] fetchCustomer', error);
    yield put(fetchCustomerFailure(error));
    yield call(HandleErrorService.showToastError, error);
  } finally {
    yield put(setLoading('fetchCustomer', false));
  }
}

export function* confirmCustomerOptionCommercialSaga(action: CustomerActionType): Generator<*, *, *> {
  if (action.type !== 'CONFIRM_CUSTOMER_OPTION_COMMERCIAL') {
    return;
  }
  yield put(setLoading('confirmCustomerOptionCommercial', true));
  try {
    const customer = yield select(customerSelector);
    const updatedCustomerPreference = customer.login.customerPreference;
    const now = Date.now().toString();
    updatedCustomerPreference.characteristics.optionInternalCommercial = action.payload.customerOptionCommercial
      .optionInternalCommercial
      ? now
      : null;

    updatedCustomerPreference.characteristics.optionExternalCommercial = action.payload.customerOptionCommercial
      .optionExternalCommercial
      ? now
      : null;
    
    yield call([tripica, 'updateCustomerPreference'], updatedCustomerPreference);
    yield put(setCustomerOptionCommercial(updatedCustomerPreference.characteristics));
    yield call(Navigation.dispatch, CommonActions.goBack());
  } catch (error) {
    WizzeeLog.warn('[saga] confirmCustomerOptionCommercialSaga', error);
    HandleErrorService.showToastError(error);
  } finally {
    yield put(setLoading('confirmCustomerOptionCommercial', false));
  }
}

export default function*(): Generator<*, *, *> {
  yield all([takeEvery('GET_CUSTOMER_OFFER', getCustomerOffer)]);
  yield all([takeEvery('CONFIRM_CUSTOMER_OPTION_COMMERCIAL', confirmCustomerOptionCommercialSaga)]);
  yield all([takeEvery('FETCH_CUSTOMER', fetchCustomerSaga)]);
}
